import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faTrash,
  faCog,
  faSyncAlt,
  faSearch,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { RateLimitAlertContext } from "../contexts/RateLimitAlertContext";
import { AdminAccessAlertContext } from "../contexts/AdminAccessAlertContext";

const BillingCorporateConfigurationTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    corpCode: "",
    networkAccessFee: "",
    adminFee: "",
    billingType: "",
    isVatable: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "corpCode",
    direction: "asc",
  });

  const { openAlert: openRateLimitAlert } = useContext(RateLimitAlertContext);
  const { openAlert: openAdminAccessAlert } = useContext(AdminAccessAlertContext);

  const columns = [
    { key: "corpCode", label: "Corporate Code", sortable: true },
    { key: "networkAccessFee", label: "Network Access Fee", sortable: true },
    { key: "adminFee", label: "Admin Fee", sortable: true },
    { key: "cardPrintingFee", label: "Card Printing Fee", sortable: true },
    { key: "cardReplacementFee", label: "Card Replacement Fee", sortable: true },
    { key: "interestRate", label: "Interest Rate", sortable: true },
    { key: "paymentTerms", label: "Payment Terms", sortable: true },
    { key: "billingType", label: "Billing Type", sortable: true },
    { key: "isVatable", label: "VAT Status", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ];

  useEffect(() => {
    fetchData();
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/billing-corporate-configurations`, {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          ...filters,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      setData(response.data.data.data);
      setTotalPages(response.data.data.last_page);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      if (error.response?.status === 403) openAdminAccessAlert();
      if (error.response?.status === 429) openRateLimitAlert();
      Swal.fire("Error!", "Failed to fetch data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2) startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            currentPage === i
              ? "bg-indigo-600 text-white"
              : "text-gray-700 hover:bg-indigo-100"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const handleDetails = (id) => {
    // navigate(`/billing-corporate-configurations/details/${id}`);
    navigate(`/corporate-settings/details/${id}`);
  };

  const handleEdit = (corp_code) => {
    // navigate(`/billing-corporate-configurations/edit/${corpCode}`);
    navigate(`/corporate-settings/setup/${corp_code}`);
  };


  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to delete this configuration record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${BASE_URL}/billing-corporate-configurations/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        Swal.fire("Deleted!", "The record has been deleted.", "success");
        fetchData();
      } catch (error) {
        Swal.fire("Error!", "Failed to delete the record.", "error");
      }
    }
  };

  const LoadingSkeleton = ({ rows = 5, columns = 5 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="px-6 py-4">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6 animate-fade-in">
          <div className="p-3 bg-white rounded-2xl shadow-lg">
            <FontAwesomeIcon icon={faCog} className="text-indigo-600 text-lg" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Billing Corporate Configuration</h1>
            <p className="text-sm text-gray-500">Manage billing configurations for corporate accounts</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden animate-slide-up">
          {/* Top Controls */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="relative flex-1">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search billing configurations..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={fetchData}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all duration-200 shadow-sm"
                >
                  <FontAwesomeIcon icon={faSyncAlt} />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-100 overflow-x-auto">
            <div className="flex gap-4 min-w-max">
              {columns.map(
                (col) =>
                  col.key !== "actions" && (
                    <div key={col.key} className="flex-1 min-w-[150px]">
                      {col.key === "billingType" ? (
                        <select
                          value={filters[col.key]}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        >
                          <option value="">All Billing Types</option>
                          <option value="SI">Sales Invoice</option>
                          <option value="SOA">Statement of Account</option>
                        </select>
                      ) : col.key === "isVatable" ? (
                        <select
                          value={filters[col.key]}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        >
                          <option value="">All VAT Status</option>
                          <option value="1">Vatable</option>
                          <option value="0">Non-Vatable</option>
                        </select>
                      ) 
                      : (
                        <input
                          type="text"
                          placeholder={`Filter by ${col.label}`}
                          value={filters[col.key]}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        />
                      )
                      }
                    </div>
                  )
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                        column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                      }`}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="flex items-center gap-1">
                        {column.label}
                        {column.sortable && (
                          <div className="flex flex-col text-xs">
                            <span
                              className={`${
                                sortConfig.key === column.key && sortConfig.direction === "asc"
                                  ? "text-indigo-600"
                                  : "text-gray-400"
                              }`}
                            >
                              ▲
                            </span>
                            <span
                              className={`${
                                sortConfig.key === column.key && sortConfig.direction === "desc"
                                  ? "text-indigo-600"
                                  : "text-gray-400"
                              }`}
                            >
                              ▼
                            </span>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-6 py-6 text-center text-gray-500 text-sm"
                    >
                      No billing configuration data available.
                    </td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-indigo-50 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 text-sm text-gray-700">{row.corpCode}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.networkAccessFee}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.adminFee}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.cardPrintingFee}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.cardReplacementFee}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.interestRate}%</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.paymentTerms} days</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{row.billingType}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            row.isVatable
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {row.isVatable ? "Vatable" : "Non-Vatable"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-2">
                          {/* <button
                            onClick={() => handleDetails(row.id)}
                            className="px-3 py-1 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all shadow-sm"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Details
                          </button> */}
                          <button
                            onClick={() => handleEdit(row.corpCode)}
                            className="px-3 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-sm"
                          >
                            <FontAwesomeIcon icon={faCog} className="mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(row.id)}
                            className="px-3 py-1 text-xs text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all shadow-sm"
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center justify-between p-6 border-t border-gray-100">
            <div className="mb-3 md:mb-0">
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>
                    {val} per page
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
              >
                <FontAwesomeIcon icon={faAngleDoubleLeft} />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              {renderPageNumbers()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
              >
                <FontAwesomeIcon icon={faAngleDoubleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingCorporateConfigurationTable;